import { useState } from "react";
import { AlertTriangle, ChevronDown, X } from "lucide-react";

import { cn } from "@/design-system/cn.ts";
import { dismissSchemaError, useSchemaErrors, type SchemaError } from "./schema-error.ts";

/**
 * Toasts for contract failures. Deliberately sticky — no auto-dismiss timer —
 * because a schema mismatch is a defect someone needs to see, not a transient
 * notice. Collapsed it says only what failed; tapping it reveals the full zod
 * report, which is also already in the console.
 *
 * Anchored top-centre: the console owns the top-left on desktop, and on mobile
 * this clears the top bar rather than fighting the tool dock at the bottom.
 */
export function SchemaErrorToasts() {
  const errors = useSchemaErrors();
  if (errors.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-3"
      style={{ top: "max(1rem, calc(env(safe-area-inset-top) + 4.25rem))" }}
      role="region"
      aria-label="Schema errors"
    >
      {errors.map((e) => (
        <Toast key={e.id} error={e} />
      ))}
    </div>
  );
}

function Toast({ error }: { error: SchemaError }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        "console pointer-events-auto w-full max-w-lg overflow-hidden rounded-console",
        "animate-in duration-200 fade-in-0 slide-in-from-top-2",
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-2.5 p-3">
        <AlertTriangle className="mt-px size-4 shrink-0 text-alert" />

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="min-w-0 flex-1 text-left"
        >
          <span className="block text-[13px] leading-snug font-medium text-chalk">
            Failed to parse {error.label}
          </span>
          <span className="mt-0.5 flex items-center gap-1 font-mono text-[9px] tracking-[0.12em] text-chalk-mute uppercase">
            <ChevronDown
              className={cn("size-2.5 transition-transform", open && "rotate-180")}
              aria-hidden="true"
            />
            {open ? "hide detail" : "show detail"}
          </span>
        </button>

        <button
          type="button"
          onClick={() => dismissSchemaError(error.id)}
          aria-label={`Dismiss ${error.label} error`}
          className="grid size-6 shrink-0 place-items-center rounded-md border border-rule text-chalk-dim transition hover:border-rule-bright hover:text-chalk active:scale-95"
        >
          <X className="size-3" />
        </button>
      </div>

      {open && (
        <pre className="max-h-64 overflow-auto border-t border-rule bg-ink-950/50 px-3 py-2.5 font-mono text-[10.5px] leading-relaxed whitespace-pre-wrap text-chalk-dim">
          {error.detail}
        </pre>
      )}
    </div>
  );
}
